import { AxiosRequestConfig } from "axios";
import { PenaltyType } from "../models/enum/penaltyType";
import { TicketCheckInStatus } from "../models/enum/ticketCheckInStatus";
import { WinningType } from "../models/enum/winningType";
import { WinnerIdInfo } from "../models/winnerIdInfo";
import { NoticeCreateDto, NoticeModifyDto } from "../models/notice";
import { LoginType } from "../models/enum/login";

export namespace OnSiteApi {
	export const weAuthLogin = (): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/authentication/login/weauth/proxy",
			method: "GET",
		};
	};

	export const weAuthRequestOtp = (): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/authentication/login/${LoginType.WEAUTH}/otp`,
			method: "POST",
		};
	};

	export interface OneDayStaffLoginDto {
		verifyToken: string;
	}

	export const oneDayStaffLogin = ({
		email,
		eventId,
	}: {
		email: string;
		eventId: number;
	}): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/authentication/login/${LoginType.ONE_DAY_STAFF}/otp`,
			method: "POST",
			data: {
				email,
				eventId,
			},
		};
	};

	export const backstageLogin = ({
		token,
		artistId,
		partnerId,
	}: {
		token: string;
		artistId: number;
		partnerId: number;
	}): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/authentication/login/backstage",
			method: "POST",
			data: {
				token,
				artistId,
				partnerId,
			},
		};
	};

	export const logout = (): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/authentication/logout",
			method: "POST",
		};
	};

	export const verifyOtp = (
		otpCode: string,
		verifyToken: string,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/authentication/otp/verify`,
			method: "POST",
			headers: { Authorization: `Bearer ${verifyToken}` },
			data: { otpCode },
		};
	};

	export const events = (
		artistId?: number,
		eventId?: number,
		page?: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events`,
			...(artistId !== undefined || page !== undefined
				? {
						params: {
							...(eventId && { eventId: eventId }),
							...(Number.isInteger(page) && { page: page }),
						},
					}
				: {}),
		};
	};

	export const event = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}`,
		};
	};

	export const checkInStatus = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/check-in-status`,
		};
	};

	export const artists = (): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/artists",
		};
	};

	export const checkIn = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		winnerId: number,
		isTardy: boolean,
		ticketCode: string,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/${winnerId}/check-in`,
			method: "PUT",
			data: { ticketCode, isTardy },
		};
	};

	export const cancelCheckin = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		winnerId: number,
		ticketCode: string,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/${winnerId}/check-in`,
			method: "DELETE",
			data: { ticketCode },
		};
	};

	interface UserTicketQueryDto {
		ticketCode: string;
	}

	interface UserOrderQueryDto {
		winnerType: WinningType;
		order: number;
	}

	interface UserMembershipQueryDto {
		winnerType: WinningType;
		membershipNumber: string;
	}

	export const queryWinnerByTicket = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		params: UserTicketQueryDto,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners`,
			method: "GET",
			params: { ticketCode: params.ticketCode },
		};
	};

	export const queryWinnerByOrder = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		params: UserOrderQueryDto,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/checked-in/sequence`,
			method: "GET",
			params: {
				winningCheckInType: params.winnerType,
				winnerCheckInSequenceNumber: params.order,
			},
		};
	};

	export const queryWinnerByMembership = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		params: UserMembershipQueryDto,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/checked-in/membership`,
			method: "GET",
			params: {
				winningCheckInType: params.winnerType,
				winnerCheckInMembershipNumber: params.membershipNumber,
			},
		};
	};

	export const penalize = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		penaltyType: PenaltyType,
		fanId: number,
		winnerId: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/violations`,
			method: "POST",
			data: { fanId, winnerId, type: penaltyType },
		};
	};

	export const cancelPenalty = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		penaltyType: PenaltyType,
		violationId: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/violations/${violationId}`,
			method: "DELETE",
			data: { type: penaltyType },
		};
	};

	export const fansByTicketCheckInStatus = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		status: TicketCheckInStatus,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winners/sequence-numbers`,
			method: "GET",
			params: { ticketStatus: status },
		};
	};

	export const registerNoShow = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		winnerIds: WinnerIdInfo[],
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/no-shows`,
			method: "POST",
			data: { noShowWinners: winnerIds },
		};
	};

	export const postMemo = (
		eventId: number,
		eventRewardId: number,
		eventCheckInId: number,
		memo: string,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/events/${eventId}/rewards/${eventRewardId}/checkIns/${eventCheckInId}/memo`,
			method: "POST",
			data: { body: memo },
		};
	};

	export const leaveNotice = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		notice: NoticeCreateDto,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v2/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winner-notices`,
			method: "POST",
			data: notice,
		};
	};

	export const modifyNotice = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
		noticeId: number,
		notice: NoticeModifyDto,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v2/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winner-notices/${noticeId}`,
			method: "PUT",
			data: notice,
		};
	};

	export const noticeList = (
		artistId: number,
		eventId: number,
		eventRewardId: number,
	): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v2/artists/${artistId}/events/${eventId}/rewards/${eventRewardId}/winner-notices`,
			method: "GET",
		};
	};

	export const photoInfraSession = (): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/storage/session-key",
			method: "GET",
		};
	};

	export const getSecurityPledge = (): AxiosRequestConfig => {
		return {
			url: "/onsite-api/v1/authentication/security-pledge/latest",
			method: "GET",
		};
	};
	export const agreeSecurityPledge = (version: string): AxiosRequestConfig => {
		return {
			url: `/onsite-api/v1/authentication/security-pledge/${version}/agree`,
			method: "POST",
		};
	};
}
